import React, { useState, useEffect } from 'react';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useZnoozinuToken } from '../hooks/useZnoozinuToken';
import CosmicNotification from './CosmicNotification';
import './CosmicForgePage.css';

// Avatar tiers with pricing
const AVATAR_TIERS = {
  STELLAR: {
    name: 'Stellar Avatar',
    price: 1000,
    icon: '🌟',
    description: 'Basic cosmic design with starfield backgrounds and ethereal forms',
    rarity: 'Common',
    features: ['Starfield Background', 'Ethereal Form', 'Basic Cosmic Glow']
  },
  GALACTIC: {
    name: 'Galactic Avatar',
    price: 2500,
    icon: '🌌',
    description: 'Enhanced with nebula effects and crystalline forms',
    rarity: 'Uncommon',
    features: ['Nebula Storm Background', 'Crystalline Form', 'Galaxy Spiral Aura', 'Cosmic Helmet']
  },
  NEBULA: {
    name: 'Nebula Avatar',
    price: 5000,
    icon: '⭐',
    description: 'Epic design with particle effects and quantum forms',
    rarity: 'Rare',
    features: ['Supernova Background', 'Quantum Form', 'Reality Distortion', 'Void Crown', 'Warp Field Effects']
  },
  QUANTUM: {
    name: 'Quantum Avatar',
    price: 10000,
    icon: '🌠',
    description: 'Legendary with animated elements and reality manipulation',
    rarity: 'Legendary',
    features: ['Multiverse Background', 'Quantum God Form', 'Omnipotent Aura', 'Reality Manipulator', 'Existence Field']
  }
};

const CosmicForgePage = ({ onClose }) => {
  const { address } = useOnboarding();
  const { balance, loading: tokenLoading } = useZnoozinuToken(address);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState('STELLAR');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState('');
  const [mintedNFT, setMintedNFT] = useState(null);
  const [notification, setNotification] = useState({
    isVisible: false,
    type: 'success',
    title: '',
    message: '',
    icon: ''
  });
  const [testMode, setTestMode] = useState(true); // Enable test mode by default for development

  // Use onboarding context for wallet detection
  const effectiveWalletAddress = address;

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [address, effectiveWalletAddress]);

  // Notification helper functions
  const showNotification = (type, title, message, icon = '') => {
    setNotification({
      isVisible: true,
      type,
      title,
      message,
      icon
    });
  };

  const dismissNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  // Real blockchain NFT creation function
  const createRealNFT = async (tierData, traits, walletAddress, testMode = true) => {
    try {
      console.log('🔗 Creating real Solana NFT transaction...');
      
      // Import Solana dependencies dynamically to avoid hook issues
      const { Connection, PublicKey, Transaction, TransactionInstruction, SystemProgram } = await import('@solana/web3.js');
      const { TOKEN_PROGRAM_ID, createTransferInstruction, getAssociatedTokenAddress, createAssociatedTokenAccountInstruction, getAccount } = await import('@solana/spl-token');
      
      // Initialize connection
      const connection = new Connection('https://api.devnet.solana.com', 'confirmed');
      
      // Get wallet from window.solana (Phantom)
      if (!window.solana || !window.solana.isConnected) {
        throw new Error('Phantom wallet not connected');
      }
      
      const wallet = window.solana;
      const publicKey = wallet.publicKey;
      
      console.log('💰 Wallet connected:', publicKey.toString());
      console.log('🔍 Wallet publicKey type:', typeof publicKey, publicKey);
      
      // Ensure publicKey is a PublicKey object
      const userPublicKey = typeof publicKey === 'string' ? new PublicKey(publicKey) : publicKey;
      
      // Contract creator wallet - use the correct address from solanaService.js
      const CONTRACT_CREATOR_WALLET = new PublicKey('9r3aFfNuu8pqG3jEkjDNhpm6BW7p87Qe2XHKaCUkRCip');
      const ZNOOZINU_MINT = new PublicKey('5cLKdANQXzUPMXgpba19Zwprhmi8yQW6R5D66oTQZ9ZN');
      
      console.log('✅ Public keys created successfully');
      
      // Calculate fees
      const baseFee = 10000; // 0.00001 SOL
      const creatorFee = Math.floor(baseFee * 0.001); // 0.1% of base fee
      const totalSOLFee = baseFee + creatorFee;
      const znoozinuAmount = tierData.price * 1000000000; // Convert to smallest unit (9 decimals)
      
      console.log('💸 Transaction costs:', {
        solFee: totalSOLFee + ' lamports',
        znoozinuCost: tierData.price + ' ZNOOZINU'
      });
      
      // Create NFT metadata
      const nftMetadata = {
        name: `${tierData.name} Cosmic Avatar #${Date.now()}`,
        symbol: 'COSMIC',
        description: tierData.description,
        image: '', // TODO: Generate/upload actual avatar image
        attributes: Object.entries(traits).map(([trait_type, value]) => ({
          trait_type: trait_type.charAt(0).toUpperCase() + trait_type.slice(1),
          value
        })),
        properties: {
          category: 'image',
          creators: [{
            address: CONTRACT_CREATOR_WALLET.toString(),
            share: 100
          }]
        },
        tier: tierData.name,
        rarity: tierData.rarity,
        mintedAt: new Date().toISOString(),
        mintedBy: userPublicKey.toString()
      };
      
      // Create transaction
      const transaction = new Transaction();
      
      // Get user's ZNOOZINU token account
      const userTokenAccount = await getAssociatedTokenAddress(
        ZNOOZINU_MINT,
        userPublicKey
      );
      
      // Get contract creator's ZNOOZINU token account
      const creatorTokenAccount = await getAssociatedTokenAddress(
        ZNOOZINU_MINT,
        CONTRACT_CREATOR_WALLET
      );
      
      console.log('🔍 Checking token accounts...');
      console.log('User token account:', userTokenAccount.toString());
      console.log('Creator token account:', creatorTokenAccount.toString());
      
      // Skip token validation in test mode
      if (!testMode) {
        // Check if user's token account exists
        try {
          await getAccount(connection, userTokenAccount);
          console.log('✅ User token account exists');
        } catch (error) {
          console.log('❌ User token account does not exist:', error.message);
          throw new Error('Your ZNOOZINU token account does not exist. Please ensure you have ZNOOZINU tokens in your wallet.');
        }
      } else {
        console.log('🧪 Test mode: Skipping user token account validation');
      }
      
      // Check if creator's token account exists, create if needed (skip in test mode)
      if (!testMode) {
        let needsCreatorAccount = false;
        try {
          await getAccount(connection, creatorTokenAccount);
          console.log('✅ Creator token account exists');
        } catch (error) {
          console.log('⚠️ Creator token account does not exist, will create it');
          needsCreatorAccount = true;
        }
        
        // Create creator token account if needed
        if (needsCreatorAccount) {
          const createCreatorAccountInstruction = createAssociatedTokenAccountInstruction(
            userPublicKey, // payer
            creatorTokenAccount, // associated token account
            CONTRACT_CREATOR_WALLET, // owner
            ZNOOZINU_MINT // mint
          );
          transaction.add(createCreatorAccountInstruction);
          console.log('📝 Added creator token account creation instruction');
        }
      } else {
        console.log('🧪 Test mode: Skipping creator token account creation');
      }
      
      // Add ZNOOZINU token transfer instruction (payment) - skip in test mode
      if (!testMode) {
        const tokenTransferInstruction = createTransferInstruction(
          userTokenAccount,
          creatorTokenAccount,
          userPublicKey, // authority
          tierData.price * 1000000000, // Convert to lamports (9 decimals)
          [],
          TOKEN_PROGRAM_ID
        );
        transaction.add(tokenTransferInstruction);
        console.log(`💰 Added ZNOOZINU transfer: ${tierData.price} tokens`);
      } else {
        console.log(`🧪 Test mode: Skipping ZNOOZINU payment of ${tierData.price} tokens`);
      }
      
      // Add memo instruction with NFT metadata
      const memoInstruction = new TransactionInstruction({
        keys: [],
        programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'), // Memo program
        data: Buffer.from(`Znoospace NFT Mint: ${JSON.stringify(nftMetadata)}`, 'utf8')
      });
      transaction.add(memoInstruction);
      console.log('📝 Added NFT metadata memo instruction');
      
      // Add SOL fee transfer to contract creator
      if (creatorFee > 0) {
        const feeTransfer = SystemProgram.transfer({
          fromPubkey: userPublicKey,
          toPubkey: CONTRACT_CREATOR_WALLET,
          lamports: creatorFee
        });
        transaction.add(feeTransfer);
        console.log('💸 Added SOL fee transfer instruction');
      }
      
      // Get recent blockhash
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = userPublicKey;
      
      console.log('📝 Transaction prepared, requesting wallet signature...');
      
      // Sign and send transaction
      const signedTransaction = await wallet.signTransaction(transaction);
      const signature = await connection.sendRawTransaction(signedTransaction.serialize());
      
      console.log('🚀 Transaction sent to Solana network:', signature);
      
      // Wait for confirmation
      await connection.confirmTransaction(signature, 'confirmed');
      
      console.log('✅ NFT minting transaction confirmed on blockchain!');
      
      // Create NFT object with real blockchain data
      const realNFT = {
        id: signature.slice(0, 8), // Use first 8 chars of signature as ID
        mintAddress: signature, // Use signature as mint address for now
        tier: Object.keys(AVATAR_TIERS).find(key => AVATAR_TIERS[key].name === tierData.name), // Find tier key
        tierName: tierData.name, // Store tier name separately
        cost: tierData.price,
        traits: traits,
        metadata: nftMetadata,
        transactionSignature: signature,
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`,
        mintedAt: new Date().toISOString(),
        owner: userPublicKey.toString()
      };
      
      return {
        success: true,
        nft: realNFT,
        signature: signature,
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`,
        fees: {
          solFee: totalSOLFee,
          znoozinuCost: tierData.price
        }
      };
      
    } catch (error) {
      console.error('❌ Real NFT creation failed:', error);
      
      // Provide specific error messages
      if (error.message.includes('insufficient funds')) {
        throw new Error('Insufficient SOL or ZNOOZINU tokens for transaction');
      } else if (error.message.includes('User rejected')) {
        throw new Error('Transaction was cancelled by user');
      } else if (error.message.includes('Token account not found')) {
        throw new Error('ZNOOZINU token account not found. Please ensure you have ZNOOZINU tokens');
      } else if (error.message.includes('Phantom wallet not connected')) {
        throw new Error('Please connect your Phantom wallet');
      }
      
      throw new Error(`NFT creation failed: ${error.message}`);
    }
  };

  const generateAvatarTraits = (tierName) => {
    const tierData = AVATAR_TIERS[tierName];
    
    // Define trait pools based on tier rarity
    const traitPools = {
      Quantum: {
        background: ['Quantum Void', 'Nebula Storm', 'Cosmic Drift'],
        body: ['Energy Form', 'Quantum Entity', 'Photon Being'],
        eyes: ['Void Gaze', 'Star Points', 'Energy Orbs'],
        aura: ['Quantum Flux', 'Reality Ripples', 'Dimensional Glow'],
        accessories: ['Quantum Crown', 'Energy Bands', 'Void Crystals'],
        specialEffects: ['Particle Trail', 'Quantum Shimmer', 'Energy Wisps']
      },
      Stellar: {
        background: ['Stellar Forge', 'Galaxy Core', 'Supernova Burst', 'Binary System'],
        body: ['Stellar Form', 'Star Matter', 'Cosmic Plasma', 'Solar Entity'],
        eyes: ['Stellar Fire', 'Galaxy Swirl', 'Pulsar Glow', 'Nova Burst'],
        aura: ['Stellar Wind', 'Gravitational Field', 'Solar Flare', 'Cosmic Radiation'],
        accessories: ['Star Crown', 'Orbital Rings', 'Solar Disc', 'Stellar Fragments'],
        specialEffects: ['Solar Flares', 'Stellar Winds', 'Gravity Waves', 'Light Bending']
      },
      Cosmic: {
        background: ['Deep Space', 'Asteroid Field', 'Planetary Ring', 'Meteor Shower', 'Space Station'],
        body: ['Cosmic Wanderer', 'Space Explorer', 'Asteroid Miner', 'Void Walker', 'Star Pilot'],
        eyes: ['Cosmic Vision', 'Deep Space Gaze', 'Stellar Sight', 'Void Eyes', 'Galaxy Pupils'],
        aura: ['Cosmic Dust', 'Space Debris', 'Stellar Particles', 'Void Essence', 'Cosmic Wind'],
        accessories: ['Space Helmet', 'Cosmic Gear', 'Star Map', 'Navigation Tools', 'Cosmic Compass'],
        specialEffects: ['Thruster Glow', 'Cosmic Dust', 'Stellar Trails', 'Space Ripples', 'Void Echoes']
      }
    };

    const pool = traitPools[tierName] || traitPools.Cosmic;
    
    // Randomly select traits from the pool
    const traits = {};
    Object.keys(pool).forEach(traitType => {
      const options = pool[traitType];
      traits[traitType] = options[Math.floor(Math.random() * options.length)];
    });

    return traits;
  };

  const handleMintAvatar = async () => {
    if (!selectedTier || !effectiveWalletAddress) {
      showNotification('error', 'Wallet Required', 'Please select a tier and connect your wallet', '🔗');
      return;
    }

    // Check if user has enough ZNOOZINU tokens
    if (balance < AVATAR_TIERS[selectedTier].price) {
      showNotification('error', 'Insufficient Funds', `You need ${AVATAR_TIERS[selectedTier].price.toLocaleString()} ZNOOZINU tokens`, '💰');
      return;
    }

    setIsGenerating(true);
    setGenerationStep(1);
    showNotification('cosmic', 'Starting Generation', 'Initializing cosmic avatar forge...', '🌌');

    try {
      // Step 1: Initializing quantum forge
      setGenerationStep(1);
      showNotification('cosmic', 'Quantum Forge', 'Preparing blockchain transaction...', '⚡');
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 2: Generating avatar traits
      setGenerationStep(2);
      showNotification('cosmic', 'Avatar Traits', 'Generating cosmic traits...', '✨');
      const traits = generateAvatarTraits(selectedTier);
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 3: Creating REAL blockchain transaction
      setGenerationStep(3);
      showNotification('cosmic', 'Blockchain Transaction', 'Creating Solana NFT transaction...', '🚀');
      
      const tierData = AVATAR_TIERS[selectedTier];
      
      console.log('🌌 Starting REAL NFT minting with:', {
        tier: tierData.name,
        price: tierData.price,
        traits: traits,
        wallet: effectiveWalletAddress
      });
      
      // Create real blockchain NFT using direct Solana integration
      const mintResult = await createRealNFT(tierData, traits, effectiveWalletAddress, testMode);
      
      if (!mintResult.success) {
        throw new Error('Blockchain NFT creation failed');
      }

      // Step 4: Transaction confirmed
      setGenerationStep(4);
      showNotification('success', 'NFT Minted!', `Transaction confirmed: ${mintResult.signature.slice(0, 8)}...`, '🎉');

      console.log('✅ NFT minted successfully:', mintResult);
      
      setMintedNFT(mintResult.nft);
      setIsGenerating(false);
      
    } catch (error) {
      console.error('❌ Real NFT minting failed:', error);
      
      let errorMessage = 'Minting failed. Please try again.';
      let errorTitle = 'Minting Failed';
      
      if (error.message.includes('insufficient funds')) {
        errorMessage = 'Insufficient SOL or ZNOOZINU tokens for transaction.';
        errorTitle = 'Insufficient Funds';
      } else if (error.message.includes('User rejected')) {
        errorMessage = 'Transaction was cancelled by user.';
        errorTitle = 'Transaction Cancelled';
      } else if (error.message.includes('Wallet not connected')) {
        errorMessage = 'Please connect your wallet and try again.';
        errorTitle = 'Wallet Not Connected';
      } else if (error.message.includes('Token account not found')) {
        errorMessage = 'ZNOOZINU token account not found. Please ensure you have ZNOOZINU tokens.';
        errorTitle = 'Token Account Error';
      }
      
      showNotification('error', errorTitle, errorMessage, '❌');
      setIsGenerating(false);
      setGenerationStep(0);
    }
  };

  const handleSetAsProfile = async () => {
    try {
      // TODO: Implement profile picture update logic
      showNotification(
        'cosmic',
        'Profile Updated!',
        'Your cosmic avatar has been set as your profile picture successfully!',
        '🌌'
      );
      
      // Close after showing notification
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error setting NFT as profile:', error);
      showNotification(
        'error',
        'Profile Update Failed',
        'Failed to set your NFT as profile picture. Please try again.',
        '❌'
      );
    }
  };

  if (loading) {
    return (
      <div className="cosmic-forge-page">
        <div className="cosmic-loading">
          <div className="cosmic-spinner"></div>
          <p>Initializing Cosmic Avatar Forge...</p>
        </div>
      </div>
    );
  }

  const selectedTierData = AVATAR_TIERS[selectedTier];
  const canAfford = balance >= selectedTierData.price;

  return (
    <div className="cosmic-forge-page">
      {/* Header Section */}
      <div className="cosmic-forge-header">
        <button className="cosmic-back-button" onClick={onClose}>
          ← Back to Feed
        </button>
        
        <div className="cosmic-forge-title">
          <h1>Cosmic Avatar Forge</h1>
          <p className="cosmic-subtitle">Create unique futuristic NFT avatars with ZNOOZINU tokens</p>
          
          {/* Test Mode Toggle */}
          <div className="test-mode-toggle">
            <label className="toggle-label">
              <input 
                type="checkbox" 
                checked={testMode} 
                onChange={(e) => setTestMode(e.target.checked)}
                className="toggle-checkbox"
              />
              <span className="toggle-slider"></span>
              <span className="toggle-text">
                {testMode ? '🧪 Test Mode (No ZNOOZINU Required)' : '💰 Production Mode (ZNOOZINU Required)'}
              </span>
            </label>
          </div>
        </div>

        <div className="cosmic-user-info">
          <div className="user-wallet">
            <span className="wallet-label">Wallet:</span>
            <span className="wallet-address">
              {effectiveWalletAddress ? `${effectiveWalletAddress.slice(0, 8)}...${effectiveWalletAddress.slice(-4)}` : 'Not Connected'}
            </span>
          </div>
          <div className="user-balance">
            <span className="balance-label">ZNOOZINU Balance:</span>
            <span className="balance-amount">
              {tokenLoading ? 'Loading...' : `${balance?.toLocaleString('en-US') || 0} ZNOOZINU`}
            </span>
          </div>
        </div>
      </div>

      {/* Main Forge Content */}
      <div className="cosmic-forge-content">
        {effectiveWalletAddress ? (
          !mintedNFT ? (
            <>
              {/* Avatar Tier Selection */}
              <div className="tier-selection-section">
                <h2>Choose Your Avatar Tier</h2>
                <div className="tier-grid">
                  {Object.entries(AVATAR_TIERS).map(([key, tier]) => {
                    // Safety check for tier data
                    if (!tier) {
                      console.warn(`Missing tier data for key: ${key}`);
                      return null;
                    }
                    
                    return (
                    <div
                      key={key}
                      className={`tier-card ${selectedTier === key ? 'selected' : ''} ${!canAfford && key === selectedTier ? 'insufficient-funds' : ''}`}
                      onClick={() => setSelectedTier(key)}
                    >
                      <div className="tier-icon">{tier.icon || '❓'}</div>
                      <h3 className="tier-name">{tier.name || 'Unknown Tier'}</h3>
                      <div className="tier-price">{(tier.price || 0).toLocaleString('en-US')} ZNOOZINU</div>
                      <div className="tier-rarity">{tier.rarity || 'Unknown'}</div>
                      <p className="tier-description">{tier.description || 'No description available'}</p>
                      <div className="tier-features">
                        {(tier.features || []).map((feature, index) => (
                          <span key={index} className="feature-tag">{feature}</span>
                        ))}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Generation Status */}
              {isGenerating && (
                <div className="generation-status">
                  <div className="generation-spinner"></div>
                  <p className="generation-text">{generationStep}</p>
                </div>
              )}

              {/* Mint Button */}
              <div className="mint-section">
                <button
                  className={`mint-button ${!canAfford ? 'insufficient-funds' : ''}`}
                  onClick={handleMintAvatar}
                  disabled={isGenerating || !canAfford}
                >
                  {isGenerating ? (
                    'Forging Avatar...'
                  ) : !canAfford ? (
                    `Insufficient Funds (Need ${selectedTierData.price.toLocaleString('en-US')} ZNOOZINU)`
                  ) : (
                    `Mint ${selectedTierData.name} (${selectedTierData.price.toLocaleString('en-US')} ZNOOZINU)`
                  )}
                </button>
              </div>
            </>
          ) : (
            /* Success Screen */
            <div className="mint-success">
              <div className="success-animation">🎉</div>
              <h2>Cosmic Avatar Forged Successfully!</h2>
              <div className="minted-nft-info">
                <div className="nft-tier">{AVATAR_TIERS[mintedNFT.tier]?.icon || '🌟'} {AVATAR_TIERS[mintedNFT.tier]?.name || mintedNFT.tierName}</div>
                <div className="nft-id">Avatar #{mintedNFT.id}</div>
                <div className="nft-cost">Cost: {mintedNFT.cost.toLocaleString('en-US')} ZNOOZINU</div>
                
                <div className="final-traits">
                  <h4>Your Avatar Traits:</h4>
                  <div className="traits-grid">
                    {Object.entries(mintedNFT.traits).map(([key, value]) => (
                      <div key={key} className="trait-item">
                        <span className="trait-label">{key.charAt(0).toUpperCase() + key.slice(1)}:</span>
                        <span className="trait-value">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="success-actions">
                <button className="set-profile-button" onClick={handleSetAsProfile}>
                  Set as Profile Picture
                </button>
                <button className="close-button-alt" onClick={onClose}>
                  Close
                </button>
              </div>
            </div>
          )
        ) : (
          <div className="cosmic-connect-wallet">
            <div className="connect-message">
              <h2>🔗 Connect Your Wallet</h2>
              <p>Please connect your Solana wallet to access the Cosmic Avatar Forge</p>
              <button className="cosmic-connect-button" onClick={() => window.location.reload()}>
                Refresh Page
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cosmic Notification */}
      <CosmicNotification
        isVisible={notification.isVisible}
        onClose={dismissNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        icon={notification.icon}
        duration={4000}
      />
    </div>
  );
};

export default CosmicForgePage;
